import i18n from "@/i18n.ts";
import { ApiSorting } from "@/types/api.types.ts";
import { ApiExportFormat } from "@/types/analytics/analytics.types.ts";
import { ApiCreationSource } from "@/types/common.types.ts";
import { ApiEmployee, ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiFilterBase, ApiPagedData } from "@skailer-com/uikit/core";
import { ApiFormAggregationResult } from "@/types/forms/form-result.types.ts";
import { ApiForm, ApiFormStats } from "@/types/forms/form-view.types.ts";
import {
  ApiFormParticipantStatus,
  ApiFormResultAnswerAnonymous,
} from "@/types/forms/form.types.ts";
import { ReminderSettingsPayload } from "@/types/reminder.types.ts";
import { Rule, RuleGroup, RuleGroupShort } from "@/types/rule.types.ts";
import { ApiWorkflowShort } from "@/types/workflows/workflow.types.ts";
import { SelectItem } from "@skailer-com/uikit";

export enum ApiSurveyStatus {
  draft = "draft",
  pending_launch = "pending_launch",
  running = "running",
  paused = "paused",
  completed = "completed",
}

export const getApiSurveyStatusTitle = (status: ApiSurveyStatus): string => {
  switch (status) {
    case ApiSurveyStatus.draft:
      return i18n.t("common:draft");
    case ApiSurveyStatus.pending_launch:
      return i18n.t("common:status_pending_launch");
    case ApiSurveyStatus.running:
      return i18n.t("common:status_running");
    case ApiSurveyStatus.paused:
      return i18n.t("common:status_paused");
    case ApiSurveyStatus.completed:
      return i18n.t("surveys:survey_completed_title");
  }
};

export enum ApiSurveyType {
  survey = "survey",
  survey_with_approval = "survey_with_approval",
}

export interface ApiSurveyRespond {
  id: number;
  title: string;
  description: string | null;
  emoji: string | null;
  type: ApiSurveyType;
  status: ApiSurveyStatus;
  participant_status: ApiFormParticipantStatus;
  date_starts_at: Date | null;
  date_ends_at: Date | null;
  is_anonymous: boolean;
  target_employee: ApiEmployee | null;

  form_stats: ApiFormStats;
}

export interface ApiSurveyParticipantsStats {
  total_count: number;
  completed_count: number;
  in_progress_count: number;
  invited_count: number;
  added_count: number;
}

export interface ApiSurveyParticipant {
  status: ApiFormParticipantStatus | null;
  employee: ApiEmployee;
  datetime_last_reminder?: Date | null;
}

export interface ApiSurveyACL {
  can_edit: boolean;
}

export interface ApiSurvey {
  id: number;
  title: string;
  status: ApiSurveyStatus;
  participants_stats: ApiSurveyParticipantsStats;
  date_starts_at: Date | null;
  date_ends_at: Date | null;
  anonymous_threshold: number;
  acl: ApiSurveyACL;
}

export interface ApiSurveyShort {
  id: number;
  title: string;
  type: ApiSurveyType;
  status: ApiSurveyStatus;
  date_starts_at: Date | null;
  date_ends_at: Date | null;
  emoji: string | null;
}

export interface ApiSurveySearchItem {
  id: number;
  title: string;
  status: ApiSurveyStatus;
  participants_stats: ApiSurveyParticipantsStats;
  participants_preview: ApiEmployeeShort[];
  date_starts_at: Date | null;
  date_ends_at: Date | null;
  emoji: string | null;
  owners: ApiEmployeeShort[];
  created_by_workflow: ApiWorkflowShort | null;
  acl: ApiSurveyACL;
}

export interface ApiSurveyFilter extends ApiFilterBase {
  text?: string;
  statuses?: ApiSurveyStatus[];
  creation_source?: ApiCreationSource | null;
}

export interface ApiSurveyParticipantFilter extends ApiFilterBase {
  survey_id: number;
  text?: string;
  division_ids?: number[];
  team_ids?: number[];
  location_ids?: number[];
  employment_type_ids?: number[];
  statuses?: ApiFormParticipantStatus[];
  sorting?: ApiSorting;
  job_profile_ids?: number[];
  job_role_ids?: number[];
  department_ids?: number[];
}

export interface ApiSurveyParticipantViewFilter extends ApiFilterBase {
  text?: string;
  statuses?: ApiSurveyStatus[];
  participant_statuses?: ApiFormParticipantStatus[];
}

export interface ApiSurveyParticipantView {
  survey: ApiSurveyShort;
  participant_status: ApiFormParticipantStatus | null;
  datetime_completed_at?: Date | null;
  acl: { can_view: boolean };
  target_employee: ApiEmployee | null;
}

export type SurveyStatusItem = SelectItem;
export const SurveyStatusItems = () => {
  return [
    {
      id: ApiSurveyStatus.draft,
      title: i18n.t("common:draft"),
    },
    {
      id: ApiSurveyStatus.running,
      title: i18n.t("common:status_running"),
    },
    {
      id: ApiSurveyStatus.paused,
      title: i18n.t("common:status_paused"),
    },
    {
      id: ApiSurveyStatus.completed,
      title: i18n.t("common:status_completed"),
    },
    {
      id: ApiSurveyStatus.pending_launch,
      title: i18n.t("common:status_pending_launch"),
    },
  ] as SurveyStatusItem[];
};

export interface ApiSurveyEdit {
  id: number;
  title: string;
  status: ApiSurveyStatus;
  participants_rule_groups?: RuleGroup[] | null;
  participants_rules?: Rule[] | null;
  description: string | null;
  emoji: string | null;
  form_id?: number;
  form_template_id?: number;
  date_starts_at: Date | null;
  date_ends_at: Date | null;
  anonymous_threshold: number;
  owners: ApiEmployeeShort[];
  use_ai: boolean;
  invitation_from_employee: ApiEmployeeShort | null;
  invitation_subject: string | null;
  invitation_body: string | null;
  share_employee_rule_groups?: RuleGroup[] | null;
  share_employee_rules?: Rule[] | null;
  is_share_employee_after_completion: boolean;
  reminder_settings: ReminderSettingsPayload;
}

export interface ApiSurveyUpdateDetails {
  title: string;
  description: string | null;
  emoji: string | null;
  participants_rule_groups: RuleGroupShort[];
  owner_ids?: number[];
  use_ai?: boolean;
  invitation_from_employee_id: number | null;
  invitation_subject: string | null;
  invitation_body: string | null;
  share_employee_rule_groups: RuleGroupShort[];
  is_share_employee_after_completion?: boolean;
}

export function getSurveyRuleGroups(
  survey: ApiSurveyEdit,
  field: "participants" | "share_employee"
): RuleGroup[] {
  const groups = survey[`${field}_rule_groups`];
  if (groups != null) return groups;
  const rules = survey[`${field}_rules`] ?? [];
  return rules.length > 0 ? [{ rules }] : [];
}

export interface ApiSurveyUpdateForm {
  create_new_form?: boolean | null;
  form_template_id?: number | null;
  reset_form?: boolean | null;
}

export interface ApiSurveyUpdateLaunch {
  date_starts_at: Date | null;
  date_ends_at: Date | null;
  anonymous_threshold: number;
}

export interface ApiSurveyReopen {
  date_ends_at: Date | null;
}

export interface ApiSurveyAggregatedResults {
  form: ApiForm;
  results: ApiFormAggregationResult;
}

export interface ApiSurveyResultSearchItem {
  answer: ApiFormResultAnswerAnonymous | null;
  employee: ApiEmployee | null;
}

export interface ApiSurveyResultSearchPagedData extends ApiPagedData<ApiSurveyResultSearchItem> {
  // Ответы есть, но их меньше порога анонимности
  is_below_anonymity_threshold: boolean;
}

export interface ApiSurveyAggregatedResultsFilter extends ApiFilterBase {
  department_ids?: number[];
  job_profile_ids?: number[];
  job_role_ids?: number[];
}

export interface ApiSurveyResultsFilter extends ApiSurveyAggregatedResultsFilter {
  survey_id: number;
  form_element_id: number;
  answer_scale_option_ids?: number[];
  answer_select_option_ids?: number[];
  selectable_option_ids?: number[];
  department_ids?: number[];
  manager_ids?: number[];
}

export interface ApiSurveyChartParticipantsByPeriod {
  date_period_start: Date;
  date_period_end: Date;
  total_count: number;
  total_employee_ids: number[];
}

export interface ApiSurveyChart {
  date_start: Date;
  date_end: Date;
  completed_by_periods: ApiSurveyChartParticipantsByPeriod[];
  total_completed_participants: number;
  total_participants: number;
}

export interface ApiSurveyExportPayload {
  format: ApiExportFormat;
}
